import { useState, useEffect, useMemo } from 'react';
import { Plus, X, Loader, Edit, Trash2, ExternalLink, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import { useNotification } from '../../contexts/NotificationContext';

export default function PageCreator() {
  const [pages, setPages] = useState([]);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null, // 'create', 'edit'
    pageId: null
  });
  const [formData, setFormData] = useState({
    titles: {
      en: '',
      ru: '',
      kz: ''
    },
    route: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [loadingPageData, setLoadingPageData] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState({
    'en': 'English',
    'ru': 'Русский',
    'kz': 'Қазақша'
  });
  const { showNotification } = useNotification();

  // Состояние для поиска
  const [searchQuery, setSearchQuery] = useState('');

  // Функция для поиска страниц
  const filteredPages = useMemo(() => {
    if (!searchQuery.trim()) {
      return pages;
    }

    const query = searchQuery.toLowerCase().trim();
    return pages.filter(page => {
      // Поиск по заголовкам на всех языках
      const titleMatch = Object.values(page.title?.texts || {}).some(title =>
        title && title.toLowerCase().includes(query)
      );

      // Поиск по маршруту
      const routeMatch = page.slug && page.slug.toLowerCase().includes(query);

      // Поиск по ID
      const idMatch = page.id && page.id.toString().includes(query);

      return titleMatch || routeMatch || idMatch;
    });
  }, [pages, searchQuery]);

  // Статистика поиска
  const searchStats = useMemo(() => {
    const totalPages = pages.length;
    const filteredCount = filteredPages.length;
    const isSearching = searchQuery.trim();

    return {
      total: totalPages,
      filtered: filteredCount,
      isSearching,
      hasResults: filteredCount > 0
    };
  }, [pages, filteredPages, searchQuery]);

  useEffect(() => {
    fetchPages();
    fetchSupportedLanguages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/pages`);

      if (response.data && response.data.pages) {
        setPages(response.data.pages);
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Ошибка при загрузке страниц');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportedLanguages = async () => {
    try {
      const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/languages`);
      if (response.data && response.data.languages) {
        setSupportedLanguages(response.data.languages);
      }
    } catch (err) {
      console.error('Error fetching languages:', err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const openModal = async (type, pageId = null) => {
    console.log('🔧 Открываем модальное окно:', { type, pageId });

    setModalState({
      isOpen: true,
      type,
      pageId
    });

    // Если это редактирование страницы, загружаем данные
    if (type === 'edit' && pageId) {
      console.log('📝 Режим редактирования страницы, pageId:', pageId);
      setLoadingPageData(true);

      // Сначала очищаем форму
      setFormData({
        titles: { en: '', ru: '', kz: '' },
        route: ''
      });

      // Затем загружаем данные страницы
      await loadPageData(pageId);
    } else {
      // Для новых страниц очищаем форму
      console.log('🆕 Режим создания новой страницы');
      setFormData({
        titles: { en: '', ru: '', kz: '' },
        route: ''
      });
    }
  };

  const loadPageData = async (pageId) => {
    try {
      console.log('🔍 Загружаем данные страницы для ID:', pageId);

      if (!pageId) {
        console.error('❌ ID страницы отсутствует');
        alert('ID страницы не найден');
        setLoadingPageData(false);
        return;
      }

      // Делаем запрос к API
      const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/pages/${pageId}`);
      console.log('📡 Ответ от API:', response.data);

      const pageData = response.data;

      // Обрабатываем заголовки
      let titles = { en: '', ru: '', kz: '' };

      if (pageData.title && pageData.title.texts) {
        console.log('📝 Обрабатываем заголовки:', pageData.title.texts);
        titles = { ...titles, ...pageData.title.texts };
      }

      // Обрабатываем маршрут
      let route = '';
      if (pageData.slug) {
        route = pageData.slug.startsWith('/') ? pageData.slug : `/${pageData.slug}`;
        console.log('🛣️ Маршрут из slug:', route);
      }

      // Подготавливаем данные для формы
      const newFormData = {
        titles: titles,
        route: route
      };

      console.log('🎯 Устанавливаем данные в форму:', newFormData);

      // ВАЖНО: Устанавливаем данные в форму
      setFormData(newFormData);

      console.log('✅ Данные страницы успешно загружены и установлены в форму');

    } catch (err) {
      console.error('❌ Ошибка при загрузке данных страницы:', err);
      console.error('📄 Детали ошибки:', err.response?.data);
      alert(`Ошибка при загрузке данных страницы: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoadingPageData(false);
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      pageId: null
    });
    setLoadingPageData(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTitleChange = (languageCode, value) => {
    setFormData(prev => ({
      ...prev,
      titles: {
        ...prev.titles,
        [languageCode]: value
      }
    }));
  };

  const createPageWithMultipleTitles = async (pageSlug, titles) => {
    try {
      const titlesObject = {};
      Object.entries(titles).forEach(([lang, text]) => {
        if (text && text.trim()) {
          titlesObject[lang] = text.trim();
        }
      });

      const pageResponse = await apiPost(`${process.env.REACT_APP_API_URL}/api/pages`, {
        slug: pageSlug,
        titles: titlesObject
      });

      if (!pageResponse.data || !pageResponse.data.id) {
        throw new Error('Не удалось создать страницу');
      }

      return {
        pageId: pageResponse.data.id,
        slug: pageSlug,
        titles: titlesObject
      };
    } catch (error) {
      console.error('Ошибка при создании страницы:', error);
      throw error;
    }
  };

  const savePage = async () => {
    const { type, pageId } = modalState;
    setSaveStatus('saving');

    try {
      // Проверяем что хотя бы один заголовок заполнен
      const hasTitle = Object.values(formData.titles).some(title => title && title.trim());
      if (!hasTitle) {
        alert('Заполните хотя бы один заголовок');
        setSaveStatus(null);
        return;
      }

      if (!formData.route || !formData.route.trim()) {
        alert('Путь (route) обязателен');
        setSaveStatus(null);
        return;
      }

      if (type === 'create') {
        // Создаем новую страницу
        const routePath = formData.route.startsWith('/') ? formData.route : `/${formData.route}`;

        try {
          const pageData = await createPageWithMultipleTitles(
            routePath.substring(1),
            formData.titles
          );

          console.log('✅ Страница создана:', pageData);

          // Обновляем список страниц
          await fetchPages();

        } catch (err) {
          console.error('Ошибка при создании страницы:', err);
          alert(err.response?.data?.message || 'Произошла ошибка при создании');
          setSaveStatus('error');
          return;
        }
      }
      else if (type === 'edit') {
        try {
          console.log('🔄 Обновляем страницу с pageId:', pageId);

          // Обновляем slug страницы
          const slugToUpdate = formData.route.startsWith('/') ? formData.route.substring(1) : formData.route;
          await apiPut(`${process.env.REACT_APP_API_URL}/api/pages/${pageId}`, {
            slug: slugToUpdate
          });

          // Обновляем заголовки страницы
          const titlesObject = {};
          Object.entries(formData.titles).forEach(([lang, text]) => {
            if (text && text.trim()) {
              titlesObject[lang] = text.trim();
            }
          });

          await apiPut(`${process.env.REACT_APP_API_URL}/api/pages/${pageId}/titles`, {
            titles: titlesObject
          });

          console.log('✅ Страница успешно обновлена');

          // Обновляем список страниц
          await fetchPages();
        } catch (err) {
          console.error('Ошибка при обновлении страницы:', err);
          alert(err.response?.data?.message || 'Произошла ошибка при обновлении');
          setSaveStatus('error');
          return;
        }
      }

      setSaveStatus('success');
      closeModal();
      showNotification("Страница успешно сохранена", "success");
    } catch (err) {
      console.error('Error saving page:', err);
      setSaveStatus('error');
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту страницу?')) {
      return;
    }

    try {
      await apiDelete(`${process.env.REACT_APP_API_URL}/api/pages/${pageId}`);

      // Обновляем список страниц
      await fetchPages();
      showNotification("Страница успешно удалена", "success");
    } catch (err) {
      console.error('Error deleting page:', err);

      // Handle specific error for blocks existing on the page
      if (err.response && err.response.status === 400) {
        const blocksCount = err.response.data.blocksCount;
        const msg = `Невозможно удалить страницу. Сначала удалите ${blocksCount} блок(ов).`;
        alert(msg);
        showNotification(msg, "error");
      } else {
        showNotification("Ошибка при удалении страницы", "error");
        alert('Ошибка при удалении страницы');
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-2 font-sans">
        <div className="flex flex-col items-center justify-center h-48">
          <Loader size={24} className="animate-spin" style={{ color: 'var(--primary-color)' }} />
          <p className="mt-3 text-gray-500 text-xs">Загрузка страниц...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-2 font-sans">
      <h1 className="text-lg font-semibold text-gray-800 text-center mb-4 pb-2 border-b-2" style={{ borderColor: 'var(--primary-color)' }}>
        Управление страницами
      </h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded mb-3 text-xs">
          <strong>Ошибка!</strong> {error}
        </div>
      )}

      {saveStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded mb-3 text-xs">
          Изменения сохранены успешно!
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-3 mb-4">
        {/* Поиск */}
        <div className="mb-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск по названию, маршруту или ID..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-color)';
                e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}33`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                title="Очистить поиск"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Статистика поиска */}
          {searchStats.isSearching && (
            <div className="mt-2 text-xs text-gray-500">
              Найдено {searchStats.filtered} из {searchStats.total} страниц
            </div>
          )}
        </div>

        <button
          onClick={() => openModal('create')}
          className="w-full mb-4 flex items-center justify-center p-3 bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 rounded text-sm"
          style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
        >
          <Plus size={16} className="mr-2" />
          Создать новую страницу
        </button>

        {!searchStats.hasResults ? (
          <div className="text-center py-8 text-gray-500">
            {searchStats.isSearching ? (
              <div>
                <p className="text-sm mb-2">Страницы не найдены</p>
                <button
                  onClick={clearSearch}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Очистить поиск
                </button>
              </div>
            ) : (
              <p className="text-sm">Страницы не найдены</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredPages.map(page => (
              <div key={page.id} className="flex justify-between items-center p-3 bg-white rounded border border-gray-200 hover:bg-gray-50">
                <div className="flex-grow">
                  <div className="flex items-center mb-1">
                    <h3 className="font-medium text-gray-800 text-sm">
                      {page.title?.texts?.ru || page.title?.texts?.en || page.title?.texts?.kz || 'Без названия'}
                    </h3>
                    <span className="ml-2 text-xs text-gray-500">
                      /{page.slug}
                    </span>
                    <Link
                      to={`/admin/pages/${page.slug}`}
                      className="ml-2"
                      style={{ color: 'var(--primary-color)' }}
                      title="Открыть страницу"
                    >
                      <ExternalLink size={12} />
                    </Link>
                  </div>
                  <div className="text-xs text-gray-600">
                    <div className="flex flex-wrap gap-2">
                      {page.title?.texts?.en && (
                        <span>EN: {page.title.texts.en}</span>
                      )}
                      {page.title?.texts?.ru && (
                        <span>RU: {page.title.texts.ru}</span>
                      )}
                      {page.title?.texts?.kz && (
                        <span>KZ: {page.title.texts.kz}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    ID: {page.id}
                  </div>
                </div>
                <div className="flex gap-1 ml-4">
                  <button
                    onClick={() => openModal('edit', page.id)}
                    className="text-gray-400 hover:bg-gray-50 p-2 rounded"
                    onMouseEnter={(e) => e.target.style.color = 'var(--primary-color)'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                    title="Редактировать страницу"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => deletePage(page.id)}
                    className="text-gray-400 hover:text-red-500 hover:bg-gray-50 p-2 rounded"
                    title="Удалить страницу"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={fetchPages}
          className="flex-1 px-4 py-2 text-white rounded text-xs"
          style={{ backgroundColor: 'var(--primary-color)' }}
          onMouseEnter={(e) => e.target.style.filter = 'brightness(0.9)'}
          onMouseLeave={(e) => e.target.style.filter = 'brightness(1)'}
        >
          Обновить список
        </button>

        {searchStats.isSearching && (
          <button
            onClick={clearSearch}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50"
          >
            Очистить поиск
          </button>
        )}
      </div>

      {modalState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-5 w-[550px] max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-800">
                {modalState.type === 'create' && 'Создать страницу'}
                {modalState.type === 'edit' && 'Редактировать страницу'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            {loadingPageData ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader size={24} className="animate-spin" style={{ color: 'var(--primary-color)' }} />
                <p className="mt-3 text-gray-500 text-sm">Загрузка данных страницы...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Заголовки на разных языках
                  </label>
                  <div className="space-y-3">
                    {Object.entries(supportedLanguages).map(([code, name]) => (
                      <div key={code}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {name} ({code.toUpperCase()})
                        </label>
                        <input
                          type="text"
                          value={formData.titles[code] || ''}
                          onChange={(e) => handleTitleChange(code, e.target.value)}
                          placeholder={`Введите заголовок на ${name.toLowerCase()}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--primary-color)';
                            e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}33`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Маршрут (slug)
                  </label>
                  <input
                    type="text"
                    name="route"
                    value={formData.route || ''}
                    onChange={handleInputChange}
                    placeholder="/example-page"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--primary-color)';
                      e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}33`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {modalState.type === 'edit' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Текущий маршрут: {formData.route || 'Не установлен'}
                    </p>
                  )}
                </div>

                <button
                  onClick={savePage}
                  disabled={
                    !Object.values(formData.titles).some(title => title && title.trim()) ||
                    !formData.route ||
                    loadingPageData
                  }
                  className="w-full py-3 text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                  style={{ backgroundColor: 'var(--primary-color)' }}
                  onMouseEnter={(e) => !e.target.disabled && (e.target.style.filter = 'brightness(0.9)')}
                  onMouseLeave={(e) => e.target.style.filter = 'brightness(1)'}
                >
                  {saveStatus === 'saving' ? 'Сохранение...' : (modalState.type === 'create' ? 'Создать' : 'Сохранить')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}